import styled from 'styled-components';

import { hexWithAlpha } from 'utils/colors';

export const Card = styled.div`
  width: 100%;
  max-width: 54rem;
  background: ${({ theme }) => theme.colors.white};
  border: 0.1rem solid ${({ theme }) => theme.colors.border};
  border-radius: 2.6rem;
  box-shadow: 0 3.4rem 8rem ${({ theme }) => hexWithAlpha(theme.colors.ink, 0.13)};
  padding: 2rem;
`;

export const BannerWrap = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 7.4rem;
`;

export const Banner = styled.div`
  position: relative;
  height: 17rem;
  border-radius: 1.8rem;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.inkPanelBg};
`;

export const BannerFade = styled.div`
  position: absolute;
  inset: 0;
  background: ${({ theme }) => `linear-gradient(
    105deg,
    ${hexWithAlpha(theme.colors.ink, 0.94)} 0%,
    ${hexWithAlpha(theme.colors.ink, 0.72)} 55%,
    ${hexWithAlpha(theme.colors.ink, 0.5)} 100%
  )`};
`;

export const BannerTopLeft = styled.div`
  position: absolute;
  top: 1.8rem;
  left: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const BannerBrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

export const BannerLogoMark = styled.div<{ $accentColor?: string | null }>`
  width: 2.2rem;
  height: 2.2rem;
  border-radius: 0.7rem;
  background: ${({ theme, $accentColor }) => $accentColor || theme.colors.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.3rem;
  font-weight: 900;
  color: ${({ theme }) => theme.colors.overlayText};
  transition: background-color 0.15s ease;
`;

export const BannerWordmark = styled.div`
  font-size: 1.4rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.overlayText};
`;

export const BannerHeadline = styled.div<{ $accentColor?: string | null }>`
  font-family: 'Archivo Black', Archivo, sans-serif;
  font-size: 1.8rem;
  line-height: 1.1;
  letter-spacing: -0.03rem;
  color: ${({ theme }) => theme.colors.overlayText};
  max-width: 32rem;

  em {
    font-style: normal;
    color: ${({ theme, $accentColor }) => $accentColor || theme.colors.accent};
    transition: color 0.15s ease;
  }
`;

export const BannerHandle = styled.div`
  position: absolute;
  top: 2rem;
  right: 2rem;
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.inkPanelMutedText};
`;

export const AvatarCircle = styled.div<{
  $background: string;
  $borderColor?: string | null;
  $square?: boolean;
}>`
  position: absolute;
  left: 50%;
  bottom: -6.6rem;
  transform: translateX(-50%);
  width: 12rem;
  height: 12rem;
  border-radius: ${({ $square }) => ($square ? '2.4rem' : '999rem')};
  border: 0.5rem solid ${({ theme, $borderColor }) => $borderColor || theme.colors.white};
  background: ${({ $background }) => $background};
  display: flex;
  align-items: flex-end;
  justify-content: center;
  overflow: hidden;
  box-shadow: 0 1rem 2.6rem ${({ theme }) => hexWithAlpha(theme.colors.ink, 0.18)};
  transition:
    border-color 0.15s ease,
    border-radius 0.15s ease;
`;

export const Body = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.2rem;
`;

export const Name = styled.div`
  font-size: 2.2rem;
  font-weight: 800;
  letter-spacing: -0.03rem;
`;

export const OnlineRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`;

export const OnlineDot = styled.div`
  width: 1rem;
  height: 1rem;
  border-radius: 999rem;
  background: ${({ theme }) => theme.colors.successBright};
`;

export const OnlineLabel = styled.div`
  font-size: 1.3rem;
  color: ${({ theme }) => theme.colors.steelGrey};
`;

export const Subtitle = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  text-align: center;
`;

export const Tagline = styled.div`
  font-size: 1.3rem;
  font-style: italic;
  color: ${({ theme }) => theme.colors.slateLight};
  text-align: center;
`;

export const MetricsRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  padding: 1.4rem 0;
  border-top: 0.1rem solid ${({ theme }) => theme.colors.surface};
  border-bottom: 0.1rem solid ${({ theme }) => theme.colors.surface};
  margin-top: 0.4rem;
`;

export const Metric = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.2rem;
`;

export const MetricValue = styled.div`
  font-size: 1.4rem;
  font-weight: 900;
`;

export const MetricLabel = styled.div`
  font-size: 1.1rem;
  color: ${({ theme }) => theme.colors.slateMuted};
`;

export const MetricDivider = styled.div`
  width: 0.1rem;
  height: 3rem;
  background: ${({ theme }) => theme.colors.surface};
`;

export const PrimaryCta = styled.button<{ $accentColor?: string | null }>`
  width: 100%;
  background: ${({ theme, $accentColor }) => $accentColor || theme.colors.greenCta};
  color: ${({ theme }) => theme.colors.overlayText};
  font-size: 1.6rem;
  font-weight: 800;
  text-align: center;
  padding: 1.8rem 0;
  border-radius: 1.4rem;
  border: none;
  cursor: pointer;
  transition: background-color 0.15s ease;
  margin-top: 1.4rem;
  font-family: inherit;
`;
